import { formatDisplayDateTime } from "../../utils/dateUtils";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import {
  ServerDeletedFilterValue,
  ServerStatusFilterValue,
} from "../../shared/ServerListFilters";
import ServerStatusPills from "../../shared/ServerStatusPills";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { insurance, Datum } from "../../interface/InterfaceInsurance";
import { InsuranceAxiosInstance } from "../../Network/InsuranceAxiosInstance";
import FileUpload from "Pages/uploadFile/InsurancePlan/InsurancePlan";
import { ScrollArea } from "../../components/ui/scroll-area";
import PlanPolicySelector from "Pages/Shared/PlanPolicy";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { Option } from "interface/Option";
import useQaAutomationIds from "../../utils/useQaAutomationIds";


// The linked entity's status is independent from the parent relation's status.
const linkedEntityInactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  if (item.__inactive === true || item.isInactive === true || item.is_deleted === true) return true;
  const status = item.is_active ?? item.Is_active ?? item.active ?? item.status ?? item.Status;
  return status === false || status === 0 || status === "0" ||
    ["false", "inactive", "disabled"].includes(String(status ?? "").trim().toLowerCase());
};

const statusListFromResponse = (response: any): any[] => {
  const data = response?.data;
  const nested = data?.result ?? data?.data ?? data?.rows ?? data;
  if (Array.isArray(nested)) return nested;
  for (const key of ["result", "data", "rows", "getPolicy", "getNetwork", "getInsurancePlans", "getInsurancePlan"]) {
    if (Array.isArray(nested?.[key])) return nested[key];
  }
  return [];
};

// Read both states, because some catalogues default to returning only active records.
const loadLinkedCatalogue = async (client: any, endpoint: string): Promise<any[]> => {
  const all = new Map<string, any>();
  for (const active of ["true", "false"]) {
    for (let page = 1; page <= 30; page++) {
      const response = await client.get(endpoint, { params: { page, limit: 100, active, is_active: active, is_deleted: "false" } });
      const rows = statusListFromResponse(response);
      rows.forEach((item: any) => {
        if (item && typeof item === "object") {
          const id = item.id ?? item.policyDbId ?? item.dbId;
          if (id != null) all.set(String(id), item);
        }
      });
      const pagination = response?.data?.pagination ?? response?.data;
      const totalPages = Number(pagination?.totalPages ?? pagination?.total_pages);
      if ((Number.isFinite(totalPages) && totalPages > 0 && page >= totalPages) || rows.length < 100) break;
    }
  }
  return Array.from(all.values());
};

const linkStatus = (entity: any, catalogue: any[], idKeys: string[]): any => {
  if (!entity || typeof entity !== "object") return entity;
  const id = entity.id ?? entity.dbId ?? entity.policyDbId ?? entity.value;
  const match = catalogue.find((row: any) =>
    id != null && idKeys.some(key => row[key] != null && String(row[key]) === String(id))
  );
  return match ? { ...entity, ...match, __inactive: linkedEntityInactive(match) } : entity;
};

const inactiveChipClass = (item: any, normal: string): string =>
  linkedEntityInactive(item)
    ? "bg-gray-200 text-gray-600 border-gray-300"
    : normal;

interface InsurancePolicy {
  id?: string | number;
  Policy_id: string;
  Policy_name: string;
  is_active?: boolean | string;
  status?: string;
  __inactive?: boolean;
}

interface InsuranceNetwork {
  id?: string | number;
  Network_name: string;
}

const normalizePolicies = (rawPolicies: any): InsurancePolicy[] => {
  if (!Array.isArray(rawPolicies)) return [];

  return rawPolicies
    .map((policy: any) => {
      if (policy && typeof policy === "object") {
        return {
          ...policy,
          id: policy.id,
          Policy_id: String(
            policy.Policy_id ?? policy.policy_id ?? policy.id ?? ""
          ),
          Policy_name:
            policy.Policy_name ??
            policy.policy_name ??
            policy.name ??
            policy.label ??
            "",
        };
      }

      return {
        Policy_id: String(policy ?? ""),
        Policy_name: "",
      };
    })
    .filter((policy) => policy.Policy_id);
};

const normalizeNetworks = (rawNetworks: any): InsuranceNetwork[] => {
  const list = Array.isArray(rawNetworks)
    ? rawNetworks
    : rawNetworks == null
      ? []
      : [rawNetworks];

  return list
    .map((network: any) => {
      if (network && typeof network === "object") {
        return {
          id: network.id,
          Network_name: String(
            network.Network_name ??
              network.network_name ??
              network.name ??
              network.label ??
              ""
          ),
        };
      }

      return {
        Network_name: String(network ?? ""),
      };
    })
    .filter((network) => network.Network_name);
};

const normalizeInsurancePlanRow = (item: any, fallback?: any): Datum => {
  const source = { ...(fallback || {}), ...(item || {}) };

  const policy = normalizePolicies(
    item?.policy ??
      item?.Policy ??
      item?.policy_id ??
      fallback?.policy ??
      fallback?.Policy ??
      fallback?.policy_id ??
      []
  );

  const network = normalizeNetworks(
    item?.Network ??
      item?.network ??
      fallback?.Network ??
      fallback?.network ??
      []
  );

  const rawStatus =
    item?.Status ??
    item?.status ??
    fallback?.Status ??
    fallback?.status;

  return {
    ...source,
    id: item?.id ?? fallback?.id,
    plan_name:
      item?.Plan_name ??
      item?.plan_name ??
      fallback?.Plan_name ??
      fallback?.plan_name ??
      "",
    policy,
    policy_id: policy.map((entry) => entry.Policy_id),
    network,
    status:
      rawStatus === true ||
      rawStatus === 1 ||
      rawStatus === "1" ||
      rawStatus === "true" ||
      rawStatus === "Active" ||
      rawStatus === "active",
    is_deleted: Boolean(
      item?.is_deleted ??
        item?.Is_deleted ??
        item?.deleted ??
        fallback?.is_deleted ??
        false
    ),
    created_by:
      item?.created_by ??
      item?.Created_by ??
      item?.createdBy ??
      fallback?.created_by ??
      null,
    createdat:
      item?.createdat ??
      item?.created_at ??
      item?.createdAt ??
      fallback?.createdat ??
      null,
    updated_by:
      item?.updated_by ??
      item?.Updated_by ??
      item?.updatedBy ??
      fallback?.updated_by ??
      [],
    updatedat:
      item?.updatedat ??
      item?.updated_at ??
      item?.updatedAt ??
      fallback?.updatedat ??
      null,
  } as Datum;
};

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// Strict Plan Name validation: every word must start with a capital letter.
// Hyphenated words are supported, e.g. "King-Fahad Plan".
const PLAN_NAME_PATTERN =
  /^[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*(?:\s+[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*)*$/;

const PLAN_NAME_FORMAT_ERROR =
  "Invalid name. Only letters are allowed and each word must start with a capital letter.";

const validatePlanName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "Plan Name is required.";
  if (trimmed.length < 3) return "Name must be at least 3 characters long";
  if (!PLAN_NAME_PATTERN.test(trimmed)) return PLAN_NAME_FORMAT_ERROR;
  return "";
};

const Insurance: React.FC = () => {
  useQaAutomationIds("insurance-plan");
  const [items, setItems] = useState<insurance>({ result: [] });
  const [Name, setName] = useState<string>("");
  const [PolicyID, setPolicyID] = useState<string[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearPolicys, setClearPolicys] = useState(false);
  const [policys, setPolicys] = useState<Option[]>([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const policyDialogRef = useRef<HTMLDialogElement | null>(null);
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<ServerStatusFilterValue>("");
  const [filterDeleted, setFilterDeleted] = useState<ServerDeletedFilterValue>("false");
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceTargetItem, setForceTargetItem] = useState<Datum | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const insurancePlanDetailsInFlight = useRef<Record<string, Promise<Datum>>>({});
  const [livePolicies, setLivePolicies] = useState<any[]>([]);
  const refreshPolicies = async (): Promise<any[]> => {
    try {
      const catalog = await loadLinkedCatalogue(PolicyAxiosInstance, "getPolicy");
      setLivePolicies(catalog);
      return catalog;
    } catch (error) {
      console.warn("Policy status refresh failed; using linked status from the relation", error);
      return livePolicies;
    }
  };
  useEffect(() => { void refreshPolicies(); }, []);
  const currentPolicies = (raw: any): InsurancePolicy[] =>
    normalizePolicies(raw).map(p => linkStatus(p, livePolicies, ["id", "policyDbId"]));

  const handlePolicyChange = (policyID: Option[]) => { setPolicys(policyID); };

  useEffect(() => { fetchUsers(); }, []);

  const clear = () => {
    setName("");
    setPolicyID([]);
    setPolicys([]);
    setClearPolicys((previous) => !previous);
    setIsActive(true);
  };

  const handleAddClick = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const handleOpenPolicyDialog = (policyIds: string[]) => { setSelectedPolicyIds(policyIds); policyDialogRef.current?.showModal(); };
  const handleClosePolicyDialog = () => { policyDialogRef.current?.close(); setSelectedPolicyIds([]); };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, {
    name: string;
    active: ServerStatusFilterValue;
    deleted: ServerDeletedFilterValue;
  }>({
    filters: { name: filterName, active: filterStatus, deleted: filterDeleted },
    fetchPage: ({ page, limit, filters }) =>
      InsuranceAxiosInstance.get("getInsurancePlan", {
        params: {
          page,
          limit,
          name: filters.name.trim() || undefined,
          active: filters.active || undefined,
          is_deleted: filters.deleted,
        },
      }),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: (item: any) => normalizeInsurancePlanRow(item),
    sortRows: (rows) =>
      [...rows].sort((a: any, b: any) => Number(b.id) - Number(a.id)),
    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({
        title: "Error",
        description: "Failed to fetch Insurance Plan",
        variant: "destructive",
      });
    },
  });

  const fetchInsurancePlanDetails = async (
    planId: string | number,
    fallback?: Datum
  ): Promise<Datum> => {
    const key = String(planId);
    const existingRequest = insurancePlanDetailsInFlight.current[key];
    if (existingRequest) return existingRequest;

    const request = (async () => {
      const response = await InsuranceAxiosInstance.get(`getInsurancePlan/${planId}`);

      const rawResult =
        response.data?.getInsurancePlans ??
        response.data?.getInsurancePlan ??
        response.data?.insurancePlan ??
        response.data?.result ??
        response.data?.data ??
        response.data;

      const rawPlan = Array.isArray(rawResult) ? rawResult[0] : rawResult;

      if (!rawPlan || typeof rawPlan !== "object") {
        throw new Error("Insurance Plan details were not returned by the backend.");
      }

      return normalizeInsurancePlanRow(rawPlan, fallback);
    })();

    insurancePlanDetailsInFlight.current[key] = request;

    try {
      return await request;
    } finally {
      delete insurancePlanDetailsInFlight.current[key];
    }
  };

  const handleOpenDialog = async (item: Datum): Promise<void> => {
    setShowAllUpdates(false);
    const catalog = await refreshPolicies();

    try {
      const details = await fetchInsurancePlanDetails(item.id, item);
      setSelectedItem({ ...details, policy: normalizePolicies((details as Datum & { policy?: InsurancePolicy[] }).policy).map(p => linkStatus(p, catalog, ["id"])) } as Datum);
    } catch (error) {
      console.error("Failed to load Insurance Plan details:", error);
      setSelectedItem(normalizeInsurancePlanRow(item));
      toast({
        title: "Warning",
        description: "Insurance Plan details could not be refreshed. Showing the available row data.",
      });
    }
  };

  const handleEditClick = async (item: Datum): Promise<void> => {
    const catalog = await refreshPolicies();
    try {
      const details = await fetchInsurancePlanDetails(item.id, item);
      handleEdit({ ...details, policy: normalizePolicies((details as Datum & { policy?: InsurancePolicy[] }).policy).map(p => linkStatus(p, catalog, ["id"])) } as Datum);
    } catch (error) {
      console.error("Failed to load Insurance Plan details for edit:", error);
      handleEdit(normalizeInsurancePlanRow(item));
      toast({
        title: "Warning",
        description: "Insurance Plan details could not be refreshed. Editing the available row data.",
      });
    }

    setIsDialogOpen(true);
  };

  const buildPolicyPayloadFromOption = (option: any): InsurancePolicy | null => {
    const rawDbId =
      option?.policyDbId ??
      option?.dbId ??
      option?.id;

    const dbId = Number(rawDbId);

    const policyId = String(
      option?.policyId ??
      option?.Policy_id ??
      option?.value ??
      (typeof option?.label === "string" ? option.label.split(" - ")[0] : "") ??
      ""
    ).trim();

    const policyName = String(
      option?.policyName ??
      option?.Policy_name ??
      (typeof option?.label === "string" && option.label.includes(" - ")
        ? option.label.substring(option.label.indexOf(" - ") + 3)
        : option?.label ?? "")
    ).trim();

    if (!Number.isFinite(dbId) || !policyId) {
      return null;
    }

    return {
      id: dbId,
      Policy_id: policyId,
      Policy_name: policyName,
    };
  };

  const toggleInsuranceStatus = async (item: Datum, force = false): Promise<void> => {
    try {
      const selectedPolicies = normalizePolicies(
        (item as any).policy ?? (item as any).Policy ?? item.policy_id ?? []
      );

      const selectedPolicyPayload: InsurancePolicy[] = selectedPolicies
        .map((policy) => {
          const dbId = Number(policy.id);

          if (!Number.isFinite(dbId)) {
            return null;
          }

          return {
            id: dbId,
            Policy_id: policy.Policy_id,
            Policy_name: policy.Policy_name || "",
          } as InsurancePolicy;
        })
        .filter((policy): policy is InsurancePolicy => policy !== null);

      if (selectedPolicyPayload.length !== selectedPolicies.length) {
        toast({
          title: "Error",
          description:
            "One or more linked Policies are missing their SQL database ID. Status cannot be updated safely.",
          variant: "destructive",
        });
        return;
      }

      const url = force ? `updateInsurancePlan?force=true` : `updateInsurancePlan`;
      const updatedItem = {
        data: {
          Plan_name: item.plan_name,
          Policy: selectedPolicyPayload,
          Status: !item.status,
          id: item.id,
        },
        token,
      };

      const response = await InsuranceAxiosInstance.put(url, updatedItem);

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceTargetItem(null);
        setForceError("");
        setForceAction(null);
        toast({
          title: "Success",
          description: `Status changed to ${!item.status ? "Active" : "Inactive"}.`,
        });
      } else {
        setForceId(item.id);
        setForceTargetItem(item);
        setForceAction("status");
        setForceError(response.data.error || "This plan is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.error || "An unexpected error occurred.";

      if (
        error.response?.status === 409 ||
        errMsg.toLowerCase().includes("used")
      ) {
        setForceId(item.id);
        setForceTargetItem(item);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({
          title: "Error",
          description: errMsg,
          variant: "destructive",
        });
      }
    }
  };

  const addItem = async (): Promise<void> => {
    setNameError("");
    setIdError("");

    const planNameValidationError = validatePlanName(Name);
    if (planNameValidationError) {
      setNameError(planNameValidationError);
      toast({
        title: "Error",
        description: planNameValidationError,
        variant: "destructive",
      });
      return;
    }

    if (idError) return;

    try {
      const selectedPolicies = policys
        .map((option: any) => buildPolicyPayloadFromOption(option))
        .filter((policy): policy is InsurancePolicy => policy !== null);

      if (selectedPolicies.length !== policys.length) {
        toast({
          title: "Error",
          description:
            "One or more selected Policies are missing their SQL database ID. Please reselect them.",
          variant: "destructive",
        });
        return;
      }

      const newPlan = [
        {
          Plan_name: Name,
          Policy: selectedPolicies,
          Status: isActive,
        },
      ];

      const payload = { data: newPlan, token };

      const response = await InsuranceAxiosInstance.post(
        "addNewInsurancePlan",
        payload
      );

      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);

        toast({
          title: "Success",
          description: "Insurance Plan Added Successfully.",
        });
      } else {
        toast({
          title: "Error",
          description:
            response.data.error || "Failed to add Insurance Plan.",
          variant: "destructive",
        });
      }
    } catch (error: AxiosError | any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    const selectedPolicies = normalizePolicies(
      (item as any).policy ?? (item as any).Policy ?? item.policy_id ?? []
    );

    setName(item.plan_name);
    setPolicyID(selectedPolicies.map((policy) => policy.Policy_id));

    setPolicys(
      selectedPolicies
        .filter((policy) => Number.isFinite(Number(policy.id)))
        .map(
          (policy) =>
            ({
              value: policy.Policy_id,
              label: `${policy.Policy_id} - ${
                policy.Policy_name || policy.Policy_id
              }`,
              policyDbId: Number(policy.id),
              dbId: Number(policy.id),
              policyId: policy.Policy_id,
              policyName: policy.Policy_name || "",
              __inactive: linkedEntityInactive(policy),
              is_active: policy.is_active,
              status: policy.status,
            }) as any
        )
    );

    setIsActive(item.status);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    const planNameValidationError = validatePlanName(Name);
    if (planNameValidationError) {
      setNameError(planNameValidationError);
      toast({
        title: "Error",
        description: planNameValidationError,
        variant: "destructive",
      });
      return;
    }

    try {
      const selectedPolicies = policys
        .map((option: any) => buildPolicyPayloadFromOption(option))
        .filter((policy): policy is InsurancePolicy => policy !== null);

      if (selectedPolicies.length !== policys.length) {
        toast({
          title: "Error",
          description:
            "One or more selected Policies are missing their SQL database ID. Please reselect them.",
          variant: "destructive",
        });
        return;
      }

      const updatedItem = {
        data: {
          Plan_name: Name,
          Policy: selectedPolicies,
          Status: isActive,
          id,
        },
        token,
      };

      const response = await InsuranceAxiosInstance.put(
        "updateInsurancePlan",
        updatedItem
      );

      if (response.data.success) {
        await fetchItems();
        clear();
        setIsDialogOpen(false);

        toast({
          title: "Success",
          description: "Insurance Plan updated successfully.",
        });
      }
    } catch (error: AxiosError | any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await InsuranceAxiosInstance.delete(`softDeleteInsurancePlan/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Insurance Plan Deleted Successfully." });
      } else {
        setForceId(id); setForceAction("softDelete");
        setForceError(response.data.error || "This plan is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Insurance Plan";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await InsuranceAxiosInstance.patch(`restoreInsurancePlan/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Insurance Plan restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore Insurance Plan", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore Insurance Plan", variant: "destructive" });
    }
  };

  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteInsurancePlan/${id}?force=true` : `deleteInsurancePlan/${id}`;
      const response = await InsuranceAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Insurance Plan Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This plan is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete Insurance Plan");
    }
  };

  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await InsuranceAxiosInstance.delete(`softDeleteInsurancePlan/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Insurance Plan force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete Insurance Plan");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete Insurance Plan");
    }
  };

  const [nameError, setNameError] = useState<string>("");
  const [idError, setIdError] = useState<string>("");

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(validatePlanName(value));
  };

  const handleDownload = (item: Datum) => {
    const policies = normalizePolicies(
      (item as any).policy ?? (item as any).Policy ?? item.policy_id ?? []
    );
    const policyValues = policies
      .map((policy) => `${policy.Policy_id} - ${policy.Policy_name || "Unnamed Policy"}`)
      .join(", ");

    const rows = ["name;policies;is_active"];
    rows.push([item.plan_name || "", policyValues, item.status ? "TRUE" : "FALSE"].join(";"));
    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `InsurancePlan${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      size: 80,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button
            variant="ghost"
            className="w-full font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="w-full text-center font-medium py-1">{currentPage * pageSize + row.index + 1}</div>
      ),
    },
    {
      accessorKey: "plan_name",
      size: 200,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button variant="ghost" className="font-bold w-full" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Plan Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center py-3 ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("plan_name")}
          </div>
        );
      },
    },
    {
      accessorKey: "policy",
      size: 300,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button variant="ghost" className="font-bold w-full" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Policy{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const policies = currentPolicies(
          (row.original as any).policy ??
            (row.original as any).Policy ??
            row.original.policy_id ??
            []
        );

        if (policies.length === 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }

        const displayedPolicies = policies.slice(0, 2);
        const remainingCount = policies.length - 2;

        return (
          <div className="w-full flex flex-wrap justify-center items-center gap-1.5 py-2">
            {displayedPolicies.map((policy) => (
              <span
                key={policy.Policy_id}
                title={`Policy ID: ${policy.Policy_id}`}
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${inactiveChipClass(policy, "bg-blue-100 text-blue-800 border-blue-200")}`}
              >
                {policy.Policy_name || policy.Policy_id}{linkedEntityInactive(policy) ? " (Inactive)" : ""}
              </span>
            ))}
            {remainingCount > 0 && (
              <button
                onClick={() =>
                  handleOpenPolicyDialog(
                    policies.map(
                      (policy) =>
                        `${linkedEntityInactive(policy) ? "[Inactive] " : ""}${policy.Policy_id}${
                          policy.Policy_name ? ` - ${policy.Policy_name}` : ""
                        }`
                    )
                  )
                }
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                +{remainingCount} more
              </button>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      size: 120,
      header: ({ column }) => (
        <div className="w-full text-center">
          <Button variant="ghost" className="font-bold w-full" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            Status{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        const statusValueStr = item.status ? "active" : "inactive";
        return (
          <div className="flex justify-center">
            <button
              data-testid={`insurance-plan-table-btn-status-toggle-${item.id}`}
              data-row-id={item.id}
              data-status={statusValueStr}
              onClick={() => !isDeleted && toggleInsuranceStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.status ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.status && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
   {
  id: "view",
  size: 100,
  header: () => (
    <div className="w-full text-center">
      <Button variant="ghost" className="font-bold w-full">View</Button>
    </div>
  ),
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="w-full flex justify-center items-center gap-2 py-2">
        <button
          data-testid={`insurance-plan-table-btn-view-details-${item.id}`}
          onClick={() => void handleOpenDialog(item)}
          className="text-blue-600 hover:text-blue-800"
          title="View Details"
        >
          <i className="fa-solid fa-eye text-lg"></i>
        </button>
      </div>
    );
  },
},
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="insurance-plan">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900" data-testid="insurance-plan-page-title">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-file-medical"></i>
          </span>
          Insurance Plan
        </h5>
      </div>

     <DataTable2
        qaIdPrefix="insurance-plan-data-table"
        columns={columns}
        data={items.result}
        searchColumns={[]}
        searchValue={filterName}
        onSearchChange={setFilterName}
        searchPlaceholder="Search..."
        searchHelpText="Plan Name, Policy ID, or Policy Name."
        addButtonLabel="Add Insurance Plan"
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        onEdit={handleEdit}
        onEditClick={handleEditClick}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <ServerStatusPills
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(0);
              }}
            />
            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />

      {/* Add/Edit Dialog */}
<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
<DialogContent
  data-testid={editIndex === null ? "insurance-plan-dialog-add-insurance-plan" : "insurance-plan-dialog-edit-insurance-plan"}
  className="max-w-[600px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
  onInteractOutside={(event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(".plan-policy-select__menu") ||
      target.closest(".plan-policy-select__menu-portal")
    ) {
      event.preventDefault();
    }
  }}
  onPointerDownOutside={(event: any) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(".plan-policy-select__menu") ||
      target.closest(".plan-policy-select__menu-portal")
    ) {
      event.preventDefault();
    }
  }}
  style={{
    boxShadow:
      "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "visible",
  }}
>
    <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "22px 28px", position: "relative", overflow: "hidden", borderRadius: "16px 16px 0 0", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", zIndex: 1 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
          <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-pen"}`} />
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{editIndex === null ? "Add Insurance Plan" : "Edit Insurance Plan"}</h2>
          </div>
      </div>
    </div>

    <ScrollArea className="flex-1" style={{ background: "#f8fafc" }}>
      <div style={{ padding: "22px 28px 32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize: 11, color: "#3b82f6" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Basic Information</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginBottom: 18 }}>
          <Label htmlFor="name" className="text-xs font-semibold text-slate-500">Plan Name</Label>
          <Input
            data-testid="insurance-plan-input-plan-name"
            id="name"
            value={Name}
            onChange={handleNameChange}
            placeholder="Plan Name is required"
            className={`mt-1.5 ${nameError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
          />
          {nameError && <p data-testid="insurance-plan-message-error-plan-name" className="text-red-500 text-xs mt-1.5 flex items-center gap-1"><i className="fa-solid fa-circle-exclamation text-[10px]" />{nameError}</p>}

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
            <Label className="text-xs font-semibold text-slate-500">Status</Label>
            <div style={{ display: "flex", gap: 8, marginTop: 8, maxWidth: 260 }}>
              <button
                data-testid="insurance-plan-btn-insurance-plan-status-active"
                type="button"
                onClick={() => setIsActive(true)}
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: isActive ? "1px solid #16a34a" : "1px solid #e2e8f0", background: isActive ? "#f0fdf4" : "#fff", color: isActive ? "#15803d" : "#64748b" }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Active
              </button>
              <button type="button" onClick={() => setIsActive(false)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: !isActive ? "1px solid #dc2626" : "1px solid #e2e8f0", background: !isActive ? "#fef2f2" : "#fff", color: !isActive ? "#b91c1c" : "#64748b" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /> Inactive
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
            <i className="fa-solid fa-shield-halved" style={{ fontSize: 11, color: "#16a34a" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Linked Policies</span>
        </div>
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
          <Label htmlFor="policy" className="text-xs font-semibold text-slate-500">Policy</Label>
          <div data-testid="insurance-plan-select-linked-policies-wrapper" className="mt-1.5"><PlanPolicySelector onChange={handlePolicyChange} clear={clearPolicys} value={policys} /></div>
        </div>
      </div>
    </ScrollArea>

    <div style={{ padding: "16px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", gap: 10, borderRadius: "0 0 16px 16px", flexShrink: 0 }}>
      <Button data-testid="insurance-plan-btn-cancel-insurance-plan" variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }} style={{ borderRadius: 10, fontWeight: 600 }}>Cancel</Button>
      <Button
        data-testid={editIndex === null ? "insurance-plan-btn-add-insurance-plan-submit" : "insurance-plan-btn-update-insurance-plan-submit"}
        disabled={Boolean(validatePlanName(Name))}
        onClick={() => { if (editIndex === null) addItem(); else updateItem(editIndex); }}
        style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 24px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}
      >
        <i className={`fa-solid ${editIndex === null ? "fa-plus" : "fa-check"} mr-2`}></i>
        {editIndex === null ? "Add Plan" : "Update Plan"}
      </Button>
    </div>
  </DialogContent>
</Dialog>

      {/* Policy IDs Dialog */}
      <dialog ref={policyDialogRef} className="policy-list-dialog">
        <div className="dialog-content">
          <button className="dialog-close-icon" onClick={handleClosePolicyDialog}>×</button>
          <h2 className="dialog-title flex items-center gap-2 text-xl font-bold text-blue-900">
            <i className="fa-solid fa-shield-halved"></i>
            All Policies
          </h2>
          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-lg p-4 border border-blue-200 mt-4">
            <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2" style={{ scrollbarWidth: "thin", scrollbarColor: "#020211ff #bbf7d0" }}>
              {selectedPolicyIds.map((id, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0">
                    <i className="fa-solid fa-id-card text-sm"></i>
                  </div>
                  <p className="text-gray-800 font-medium flex-1">{id}</p>
                  <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded">#{index + 1}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-blue-200">
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <i className="fa-solid fa-info-circle text-blue-600"></i>
                <span>Total: <strong className="text-blue-900">{selectedPolicyIds.length}</strong> Policies</span>
              </p>
            </div>
          </div>
          <button onClick={handleClosePolicyDialog} className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg transition-all">
            <i className="fa-solid fa-check mr-2"></i>Close
          </button>
        </div>
        <style>{`
          .policy-list-dialog { border: none; border-radius: 20px; padding: 0; width: 600px; max-width: 90%; box-shadow: 0 20px 50px rgba(0,0,0,0.3); background-color: #fff; }
          .policy-list-dialog .dialog-content { padding: 2rem; }
          .policy-list-dialog .dialog-close-icon { position: absolute; top: 1rem; right: 1rem; background: #1e40af; color: white; border-radius: 50%; width: 32px; height: 32px; font-size: 1.3rem; cursor: pointer; border: none; }
          .policy-list-dialog .dialog-close-icon:hover { background: #3b82f6; transform: scale(1.1); }
          .policy-list-dialog::backdrop { background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); }
        `}</style>
      </dialog>

      {/* View Details Dialog */}
<Dialog open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
  <DialogContent
    data-testid="insurance-plan-dialog-view-details"
    className="max-w-[660px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
    style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18)" }}
  >
    {selectedItem && (
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "85vh" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "18px 22px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative", zIndex: 1 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
              {selectedItem.plan_name?.charAt(0)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2 style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>{selectedItem.plan_name}</h2>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: selectedItem.status ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.status ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.status ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
                  {selectedItem.status ? "Active" : "Inactive"}
                </span>
                {(selectedItem as any).is_deleted && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                    <i className="fa-solid fa-trash" style={{ fontSize: 9 }} /> Deleted
                  </span>
                )}
              </div>
              <p style={{ fontSize: 11, color: "rgba(147,197,253,0.8)", margin: "2px 0 0" }}>
                Plan ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Plan info */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
              <i className="fa-solid fa-file-medical" style={{ fontSize: 12, color: "#3b82f6" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Plan information</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Plan name</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{selectedItem.plan_name}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Status</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600, width: "fit-content", background: selectedItem.status ? "#dcfce7" : "#fee2e2", color: selectedItem.status ? "#166534" : "#991b1b", border: `1px solid ${selectedItem.status ? "#bbf7d0" : "#fecaca"}` }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
                  {selectedItem.status ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          {/* Policies */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            {(() => {
              const policies = currentPolicies(
                (selectedItem as any).policy ??
                  (selectedItem as any).Policy ??
                  selectedItem.policy_id ??
                  []
              );

              return (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                    <i className="fa-solid fa-shield-halved" style={{ fontSize: 12, color: "#16a34a" }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Associated policies</span>
                    <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 9999, background: "#eff6ff", color: "#1d4ed8", fontWeight: 600, border: "1px solid #bfdbfe", marginLeft: "auto" }}>
                      {policies.length} policies
                    </span>
                  </div>

                  {policies.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                      {policies.map((policy, idx) => (
                        <div key={`${policy.Policy_id}-${idx}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: linkedEntityInactive(policy) ? "#e5e7eb" : "#f8fafc", border: linkedEntityInactive(policy) ? "1px solid #9ca3af" : "1px solid #e2e8f0" }}>
                          <div style={{ width: 24, height: 24, borderRadius: 6, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fa-solid fa-id-card" style={{ fontSize: 10, color: "#2563eb" }} />
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                              {policy.Policy_name || "Unnamed Policy"}{linkedEntityInactive(policy) ? " (Inactive)" : ""}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", fontFamily: "monospace" }}>
                              {policy.Policy_id}
                            </span>
                          </div>
                          <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8", background: "#f1f5f9", padding: "1px 8px", borderRadius: 6, border: "1px solid #e2e8f0" }}>#{idx + 1}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No policies assigned</span>
                  )}
                </>
              );
            })()}
          </div>

          {/* Audit Trail */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
              <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Audit trail</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 6 }}>Created by</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                    {String(resolveUserName((selectedItem as any).created_by) ?? "?")[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                </div>
              </div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 6 }}>Created on</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="fa-regular fa-calendar" style={{ fontSize: 12, color: "#3b82f6" }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                    {(selectedItem as any).createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}
                  </span>
                </div>
              </div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 6 }}>Last updated</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="fa-regular fa-clock" style={{ fontSize: 12, color: "#10b981" }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                    {(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}
                  </span>
                </div>
              </div>
            </div>

{(() => {
  const updatedBy: { id?: number; at?: string }[] = (selectedItem as any).updated_by;
  if (!Array.isArray(updatedBy) || updatedBy.length === 0) return null;
  return (
    <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
        <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
        <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
        <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
          {updatedBy.length} update{updatedBy.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(updatedBy as any[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
          const updateDate = entry.date || entry.at || entry.updatedat || entry.timestamp || (i === 0 ? (selectedItem as any)?.updatedat : undefined);
          return (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
              {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
            </div>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
              {updateDate ? formatDisplayDateTime(updateDate) : "—"}
            </span>
          </div>
        );})}
        {updatedBy.length > 4 && (
          <div style={{ textAlign: "center", padding: "6px 0" }}>
            <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
              {showAllUpdates ? "Show less ↑" : `+${updatedBy.length - 4} more updates ↓`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
})()}

          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button data-testid="insurance-plan-btn-close-details" onClick={() => setSelectedItem(null)} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "7px 24px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 13, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
            Close Details
          </button>
        </div>

      </div>
    )}
  </DialogContent>
</Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the plan.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                 <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
  <button
    onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
    style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
    Cancel
  </button>
  <button
    onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
    style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
    <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
  </button>
</div>
                </>
              )}

              {/* Soft Delete / Status */}
              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Plan Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button
                      onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status" && forceTargetItem) toggleInsuranceStatus(forceTargetItem, true);
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default Insurance;